<template>
  <div class="tiptap-color-control">
    <v-menu v-model="menuIsOpen" :close-on-content-click="false" location="bottom start">
      <template #activator="{ props }">
        <TipTapToolbarButton v-bind="props" :icon="icon" :title="title" :is-active="hasColor" />
      </template>

      <v-sheet class="tiptap-color-control__palette" elevation="4">
        <button
          type="button"
          class="tiptap-color-control__reset"
          :title="clearTitle"
          :aria-pressed="!hasColor"
          @click="clearColor"
        >
          <span
            class="tiptap-color-control__swatch tiptap-color-control__swatch--none"
            :class="{ 'is-selected': !hasColor }"
            aria-hidden="true"
          ></span>
          <span class="tiptap-color-control__reset-label">{{ clearTitle }}</span>
        </button>

        <v-divider class="my-2" />

        <div class="tiptap-color-control__heading">Theme Colours</div>
        <div class="tiptap-color-control__grid">
          <button
            v-for="swatch in themeBaseColors"
            :key="swatch.name"
            type="button"
            class="tiptap-color-control__swatch"
            :class="{ 'is-selected': swatch.value === currentColor }"
            :style="{ backgroundColor: swatch.value }"
            :title="swatch.name"
            :aria-label="swatch.name"
            :aria-pressed="swatch.value === currentColor"
            @click="applyColor(swatch.value)"
          ></button>
        </div>
        <div class="tiptap-color-control__grid tiptap-color-control__grid--variants">
          <button
            v-for="swatch in themeVariantColors"
            :key="swatch.name"
            type="button"
            class="tiptap-color-control__swatch"
            :class="{ 'is-selected': swatch.value === currentColor }"
            :style="{ backgroundColor: swatch.value }"
            :title="swatch.name"
            :aria-label="swatch.name"
            :aria-pressed="swatch.value === currentColor"
            @click="applyColor(swatch.value)"
          ></button>
        </div>

        <div class="tiptap-color-control__heading">Standard Colours</div>
        <div class="tiptap-color-control__grid">
          <button
            v-for="swatch in standardColors"
            :key="swatch.name"
            type="button"
            class="tiptap-color-control__swatch"
            :class="{ 'is-selected': swatch.value === currentColor }"
            :style="{ backgroundColor: swatch.value }"
            :title="swatch.name"
            :aria-label="swatch.name"
            :aria-pressed="swatch.value === currentColor"
            @click="applyColor(swatch.value)"
          ></button>
        </div>

        <v-divider class="my-2" />

        <v-menu v-model="rangeIsOpen" :close-on-content-click="false" location="end top">
          <template #activator="{ props: rangeActivator }">
            <button
              v-bind="rangeActivator"
              type="button"
              class="tiptap-color-control__custom"
              :title="customLabel"
              :aria-pressed="hasCustomColor"
            >
              <span
                class="tiptap-color-control__swatch tiptap-color-control__swatch--custom"
                :class="{ 'is-selected': hasCustomColor }"
                :style="{ backgroundColor: customSwatchColor }"
                aria-hidden="true"
              ></span>
              <span class="tiptap-color-control__custom-label">{{ customLabel }}</span>
            </button>
          </template>

          <v-sheet class="pa-2" elevation="4">
            <!-- Double-click on the colour field commits the point under the pointer: the first
                 click moves the handle, the second confirms. The OK button below is the same
                 action. -->
            <div class="tiptap-color-control__range-picker" @dblclick="onRangeDoubleClick">
              <v-color-picker v-model="rangeDraft" mode="hex" :modes="['hex']" />
            </div>

            <div class="d-flex justify-end ga-1 mt-2">
              <v-btn size="small" variant="text" @click="rangeIsOpen = false">Cancel</v-btn>
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                :disabled="!rangeDraftIsComplete"
                @click="applyRangeColor"
              >
                OK
              </v-btn>
            </div>
          </v-sheet>
        </v-menu>
      </v-sheet>
    </v-menu>
  </div>
</template>

<script>
import TipTapToolbarButton from '@/components/content/tiptap/TipTapToolbarButton';
import {
  THEME_COLOR_ROWS,
  STANDARD_COLORS,
  PALETTE_VALUES,
  toPaletteHex
} from '@/components/content/tiptap/colorPalette';
import resolveSelectionColor from '@/components/content/tiptap/resolveSelectionColor';

const FALLBACK_COLOR = '#000000';
const CUSTOM_LABEL = 'Custom colour…';

export default {
  name: 'TipTapColorControl',

  components: {
    TipTapToolbarButton
  },

  props: {
    editor: {
      type: Object,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    clearTitle: {
      type: String,
      required: true
    },
    // The attribute key this control drives, e.g. `color` or `backgroundColor`.
    attributeName: {
      type: String,
      required: true
    },
    // Where that attribute lives. A mark for the text controls (`textStyle`), or node types for
    // the cell fill (`tableCell`, `tableHeader`) - a header cell and a body cell are different
    // nodes, so both must be consulted to show the right active swatch.
    attributeTargets: {
      type: Array,
      default: () => ['textStyle']
    },
    // Names of the TipTap commands, so this component stays agnostic about which one it is driving.
    applyCommand: {
      type: String,
      required: true
    },
    clearCommand: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      menuIsOpen: false,
      rangeIsOpen: false,
      rangeDraft: FALLBACK_COLOR
    };
  },

  computed: {
    customLabel() {
      return CUSTOM_LABEL;
    },

    themeBaseColors() {
      return THEME_COLOR_ROWS[0];
    },

    themeVariantColors() {
      return THEME_COLOR_ROWS.slice(1).flat();
    },

    standardColors() {
      return STANDARD_COLORS;
    },

    // As authored, so that a colour which does not convert to hex still previews in the swatch and
    // still counts as "a colour is applied" rather than reading as cleared.
    currentValue() {
      return resolveSelectionColor(this.editor, this.attributeTargets, this.attributeName);
    },

    currentColor() {
      return toPaletteHex(this.currentValue);
    },

    hasColor() {
      return Boolean(this.currentValue);
    },

    hasCustomColor() {
      return this.hasColor && !PALETTE_VALUES.includes(this.currentColor);
    },

    // CSS renders the authored value as-is; only the range picker needs canonical hex.
    customSwatchColor() {
      return this.currentValue || FALLBACK_COLOR;
    },

    // The picker's hex field is free text, so OK is only offered once it holds a colour that
    // canonicalises - otherwise pressing it would do nothing and give no reason.
    rangeDraftIsComplete() {
      return Boolean(toPaletteHex(this.rangeDraft));
    }
  },

  watch: {
    // Opening the range seeds it with what is applied now, which is what puts its handle on the
    // current colour instead of on whatever was picked last.
    rangeIsOpen(isOpen) {
      if (isOpen) {
        this.rangeDraft = this.currentColor || FALLBACK_COLOR;
      }
    }
  },

  methods: {
    applyColor(value) {
      this.editor.chain().focus()[this.applyCommand](value).run();
      this.menuIsOpen = false;
    },

    // Scoped to the colour field: the picker also holds the hue slider and the hex input, where a
    // double-click is just a fast drag or a word-select and must not apply and close the range.
    onRangeDoubleClick(event) {
      if (event.target.closest('.v-color-picker-canvas')) {
        this.applyRangeColor();
      }
    },

    applyRangeColor() {
      // The OK button is disabled while the draft is incomplete; this guard covers the double-click
      // path, which reaches here without going through that button.
      if (!this.rangeDraftIsComplete) {
        return;
      }

      this.rangeIsOpen = false;
      this.applyColor(toPaletteHex(this.rangeDraft));
    },

    clearColor() {
      this.editor.chain().focus()[this.clearCommand]().run();
      this.menuIsOpen = false;
    }
  }
};
</script>

<style scoped lang="scss">
.tiptap-color-control {
  display: inline-flex;
  align-items: center;
}

.tiptap-color-control__palette {
  padding: 0.5rem;
}

/* 10 columns, matching Word's Theme Colors and Standard Colors blocks. At 1.25rem per swatch the
   menu lands around 15rem wide, which is comfortable for a toolbar dropdown. */
.tiptap-color-control__grid {
  display: grid;
  grid-template-columns: repeat(10, 1.25rem);
  gap: 0.125rem;
}

/* The variant rows sit directly under their base colour, so no horizontal gap - but they are a
   separate grid from the base row, which is what gives Word's break beneath it. */
.tiptap-color-control__grid--variants {
  margin-top: 0.375rem;
  row-gap: 0;
}

.tiptap-color-control__heading {
  font-size: 0.75rem;
  font-weight: 700;
  margin: 0.5rem 0 0.25rem;
}

.tiptap-color-control__reset,
.tiptap-color-control__custom {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.125rem;
  cursor: pointer;
  font-size: 0.8125rem;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }
}

.tiptap-color-control__swatch {
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  cursor: pointer;
  border: 1px solid rgb(189, 189, 189);
  border-radius: 2px;

  &:hover,
  &:focus-visible {
    /* Outline rather than a border change, so the swatch never resizes and the grid stays put. */
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 1px;
  }

  &.is-selected {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 1px;
  }
}

/* The conventional "no colour" indicator: an empty square struck through diagonally. */
.tiptap-color-control__swatch--none {
  flex: 0 0 auto;
  background: linear-gradient(
      to top right,
      transparent 45%,
      rgb(211, 47, 47) 45%,
      rgb(211, 47, 47) 55%,
      transparent 55%
    )
    rgb(255, 255, 255);
}

.tiptap-color-control__swatch--custom {
  /* A span, not a button - the whole row is the button that opens the range. */
  flex: 0 0 auto;
}

.tiptap-color-control__custom-label {
  font-size: 0.8125rem;
  white-space: nowrap;
}
</style>
